import { useContract, useUpdateContract } from '../../api/hooks';
import { SelectWithCaption, useToast } from '../../components/DesignSystem';
import { CURRENCIES } from '../../lib/currencies';
import { errorMessage } from '../../lib/errors';
import { findGoverningPolicy, GOVERNING_POLICIES } from '../../lib/governingPolicies';

interface ReportedCurrencyCardProps {
  contractId: string;
}

/** Reported Currency + Governing Policy (Residuals) — drives reports, advances, and SUA deductions. */
export function ReportedCurrencyCard({ contractId }: ReportedCurrencyCardProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const updateContract = useUpdateContract();

  const policy = findGoverningPolicy(contract.data?.governingPolicy);

  async function handleChange(field: 'reportedCurrency' | 'governingPolicy', value: string) {
    try {
      await updateContract.mutateAsync({ contractId, input: { [field]: value } });
    } catch (error) {
      showToast(errorMessage(error, 'Could not update currency settings'), 'error');
    }
  }

  return (
    <section className="rfp-card">
      <div className="rfp-card__head">
        <div>
          <h2 className="rfp-card__title">Reported Currency</h2>
        </div>
      </div>
      <div className="rfp-form-grid">
        <SelectWithCaption
          caption="Currency for reports, advances, and SUA deductions."
          htmlFor="reported-currency"
          label="Reported Currency"
          onChange={(value) => handleChange('reportedCurrency', value)}
          value={contract.data?.reportedCurrency ?? 'GBP'}
        >
          {CURRENCIES.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.label}
            </option>
          ))}
        </SelectWithCaption>

        <SelectWithCaption
          caption={
            policy
              ? `Performer ${policy.performerRate.toFixed(2)}% / Writer ${policy.writerRate.toFixed(2)}% (${policy.territory} only)`
              : 'No default residual policy selected.'
          }
          htmlFor="governing-policy"
          label="Governing Policy (Residuals)"
          onChange={(value) => handleChange('governingPolicy', value)}
          value={contract.data?.governingPolicy ?? ''}
        >
          <option value="">—</option>
          {GOVERNING_POLICIES.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </SelectWithCaption>
      </div>
    </section>
  );
}
